import { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Form,
  Button,  
  Alert,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_INVOICE_REQUEST } from "../store/Actions/Actions";
import { toISODateOnly } from "../utils/dateMaker";
// import { BsChevronRight, BsChevronDown } from "react-icons/bs";
import Axios from "../utils/Axios";
import { PROJECT_DETAILS_PID, BUDGET_LIST, INVOICE_LIST_BY_PROJECT, INVOICE_DETAIL } from "../config/API_Collection";
import "../styles/InvoiceModal.css";

const EditInvoiceModal = ({ show, onHide, onSuccess, onToast }) => {
  const dispatch = useDispatch();
  const { selectedInvoice, updateLoading, error } = useSelector(
    (state) => state.invoices
  );
  // Try to get current project details (may include client name)
  const project = useSelector((state) => state?.projectDetails?.project);
  console.log(
    "[EditInvoiceModal] render show=",
    show,
    " selectedInvoice?",
    !!selectedInvoice
  );

  const [formData, setFormData] = useState({
    invoice_id: "",
    client_id: "",
    project_id: "",
    invoice_title: "",
    invoice_date: "",
    due_date: "",
    billing_contact: "",
    reference: "",
    description: "",
    gst: 0.15,
    status: 0,
    invoice_items: [{ budget_id: "", amount: "" }],
  });

  // Track whether user actually submitted an update (to avoid auto-closing on initial load)
  const [didSubmit, setDidSubmit] = useState(false);
  const wasSubmittingRef = useRef(false);
  const [projectData, setProjectData] = useState(null);
  const [fetchedClientName, setFetchedClientName] = useState("");
  const [validationFields, setValidationFields] = useState({});

  // Populate form when selectedInvoice changes
  useEffect(() => {
    if (selectedInvoice && show) {
      console.log(
        "[EditInvoiceModal] populate form from selectedInvoice",
        selectedInvoice
      );
      setFormData({
        invoice_id: selectedInvoice.id || selectedInvoice.invoice_id || "",
        invoice_no: selectedInvoice.invoice_no || selectedInvoice.invoiceNo || selectedInvoice.invoice_number || selectedInvoice.reference || "",
        client_id: selectedInvoice.client_id || "",
        project_id: selectedInvoice.project_id || "",
        invoice_title: selectedInvoice.invoice_title || "",
        invoice_date: toISODateOnly(selectedInvoice.invoice_date) || "",
        due_date: toISODateOnly(selectedInvoice.due_date) || "",
        billing_contact: selectedInvoice.billing_contact || "",
        reference: selectedInvoice.reference || "",
        description: selectedInvoice.description || "",
        gst: selectedInvoice.gst || 0.15,
        status: selectedInvoice.status ?? selectedInvoice.invoice_status ?? 0,
        invoice_items: selectedInvoice.invoice_items || [
          { budget_id: "", amount: "" },
        ],
      });
      // clear any previous validation state when opening/populating the modal
      setValidationFields({});
    }
  }, [selectedInvoice, show]);

  // Handle successful update (only after a submit was made)
  useEffect(() => {
    if (wasSubmittingRef.current && !updateLoading) {
      if (!error) {
        onSuccess && onSuccess();
        onHide && onHide();
      } else {
        onToast && onToast(error, "error");
      }
      wasSubmittingRef.current = false;
      setDidSubmit(false);
    }
  }, [updateLoading, error, onSuccess, onHide, onToast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationFields[name]) {
      setValidationFields(prev => { const copy = { ...prev }; delete copy[name]; return copy; });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = Array.isArray(formData.invoice_items)
      ? [...formData.invoice_items]
      : [];
    if (index < 0 || index >= updatedItems.length) return;
    if (field === "amount") {
      const cleaned = String(value).replace(/[^0-9.\-]/g, "");
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: cleaned === "" ? "" : parseFloat(cleaned),
      };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
    }
    setFormData((prev) => ({
      ...prev,
      invoice_items: updatedItems,
    }));
    // clear budget-specific validation for this item's budget if present
    const bid = updatedItems[index]?.budget_id ?? updatedItems[index]?._idx;
    if (bid && validationFields[`budget_${bid}`]) {
      setValidationFields(prev => { const copy = { ...prev }; delete copy[`budget_${bid}`]; return copy; });
    }
  };

  const addInvoiceItem = () => {
    setFormData((prev) => ({
      ...prev,
      invoice_items: [...prev.invoice_items, { budget_id: "", amount: "" }],
    }));
  };

  const removeInvoiceItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      invoice_items: prev.invoice_items.filter((_, i) => i !== index),
    }));
  };

  const buildSubmitData = () => {
    const fieldErrors = {};
    // Validate required fields -> per-field errors
    if (!formData.invoice_id) fieldErrors.invoice_id = "Invoice ID is required.";
    if (!formData.client_id) fieldErrors.client_id = "Client is required.";
    if (!formData.project_id) fieldErrors.project_id = "Project is required.";
    if (!formData.invoice_title) fieldErrors.invoice_title = "Invoice Title is required.";

    // Validate invoice items -> require at least one valid
    const validItems = formData.invoice_items.filter(
      (item) =>
        item.budget_id &&
        item.amount !== "" && item.amount !== null &&
        !isNaN(parseInt(item.budget_id)) &&
        !isNaN(parseFloat(item.amount))
    );

    if (validItems.length === 0) {
      // mark budgets as needing amounts
      (selectedInvoice?.invoice_items || formData.invoice_items || []).forEach((it) => {
        if (it && it.budget_id) fieldErrors[`budget_${it.budget_id}`] = "Enter an amount for this budget.";
      });
    } else {
      // also validate each grouped budget has an amount
      groupedBudgets.forEach((g) => {
        const groupTotal = (g.items || []).reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        if (isNaN(groupTotal) || groupTotal === 0) {
          fieldErrors[`budget_${g.bid}`] = "Enter an amount for this budget.";
        }
      });
    }

    if (Object.keys(fieldErrors).length) {
      setValidationFields(fieldErrors);
      return null;
    }

    // Convert string values to appropriate types
    const base = {
      invoice_id: parseInt(formData.invoice_id),
      client_id: parseInt(formData.client_id),
      project_id: parseInt(formData.project_id),
      invoice_title: formData.invoice_title,
      invoice_no: formData.invoice_no,
      invoice_date: formData.invoice_date,
      due_date: formData.due_date,
      billing_contact: formData.billing_contact,
      reference: formData.reference,
      description: formData.description,
      gst: String(parseFloat(formData.gst) || 0.15),
      invoice_items: validItems.map((item) => ({
        budget_id: parseInt(item.budget_id),
        amount: parseFloat(item.amount),
      })),
    };
    return base;
  };

  const handleSaveAsDraft = (e) => {
    e.preventDefault();
    const data = buildSubmitData();
    if (!data) return;
    const submitData = { ...data, status: 0 };
    console.log("[EditInvoice] Saving as draft:", submitData);
    setDidSubmit(true);
    wasSubmittingRef.current = true;
    dispatch({ type: UPDATE_INVOICE_REQUEST, payload: submitData });
  };

  const handleSubmitForApproval = (e) => {
    e.preventDefault();
    const data = buildSubmitData();
    if (!data) return;
    const submitData = { ...data, status: 2};
    console.log("[EditInvoice] Submitting for approval:", submitData);
    setDidSubmit(true);
    wasSubmittingRef.current = true;
    dispatch({ type: UPDATE_INVOICE_REQUEST, payload: submitData });
  };

  const handleClose = () => {
    // clear inline validation state before closing so next open starts fresh
    setValidationFields({});
    onHide();
  };

  // Debug mount/props (must be before any conditional return)
  useEffect(() => {
    console.log(
      "[EditInvoiceModal] effect show=",
      show,
      " selectedInvoice id=",
      selectedInvoice?.id || selectedInvoice?.invoice_id
    );
  }, [show, selectedInvoice]);

  // Group invoice items by budget id for the accordion UI
  const groupedBudgets = useMemo(() => {
    const groups = {};
    const projBudgets = projectData?.budgets || project?.budgets || [];
    (formData.invoice_items || []).forEach((it, idx) => {
      const bid = it.budget_id ?? `_${idx}`;
      // try to find a matching project budget to get a friendly name
      const found = projBudgets.find((pb) => String(pb.bid ?? pb.id) === String(bid));
      const budgetName = found
        ? (found.budget_name || found.name || found.budget || found.title || `BD${String(bid).padStart(3, "0")}`)
        : `BD${String(bid).padStart(3, "0")}`;
      if (!groups[bid])
        groups[bid] = {
          bid,
          budget_name: budgetName,
          items: [],
        };
      groups[bid].items.push({ ...it, _idx: idx });
    });
    return Object.values(groups);
  }, [formData.invoice_items]);

  // Fetch project details if not available in Redux and we have a project id
  useEffect(() => {
    const pid =
      selectedInvoice?.project_id ||
      selectedInvoice?.projectId ||
      formData.project_id;
    if (!project && pid) {
      let isMounted = true;
      const fetchProject = async () => {
        try {
          const resp = await Axios.get(`${PROJECT_DETAILS_PID}${pid}`);
          if (isMounted) {
            const newData = resp.data?.data || resp.data;
            setProjectData((prev) => {
              try {
                const same = JSON.stringify(prev) === JSON.stringify(newData);
                return same ? prev : newData;
              } catch (e) {
                return newData;
              }
            });
          }
        } catch (e) {
          console.debug(
            "Could not fetch project details for invoice edit view",
            e?.message || e
          );
        }
      };
      fetchProject();
      return () => {
        isMounted = false;
      };
    }
  }, [project, selectedInvoice, formData.project_id]);

  // Fallback: if invoice / project don't include client_name, try invoices-by-project endpoint
  useEffect(() => {
    const existingName =
      selectedInvoice?.client_name ||
      selectedInvoice?.client ||
      project?.client_name ||
      projectData?.client_name;
    if (existingName) {
      if (fetchedClientName) setFetchedClientName("");
      return;
    }

    const pid =
      selectedInvoice?.project_id ||
      selectedInvoice?.projectId ||
      formData.project_id;
    if (!pid) return;

    let isMounted = true;
    const fetchClientName = async () => {
      try {
        // Prefer fetching the invoice detail when we have a specific invoice id
        const invId = selectedInvoice?.id || selectedInvoice?.invoice_id || formData?.invoice_id;
        if (invId) {
          const resp = await Axios.get(`${INVOICE_DETAIL}?invoice_id=${invId}`);
          const name = resp?.data?.data?.client_name || resp?.data?.client_name;
          if (isMounted && name && name !== fetchedClientName) setFetchedClientName(name);
          return;
        }

        // Fallback: use the invoices-by-project endpoint (use constant)
        const resp = await Axios.get(`${INVOICE_LIST_BY_PROJECT}?project_id=${pid}`);
        const name = resp?.data?.data?.client_name || resp?.data?.client_name;
        if (isMounted && name && name !== fetchedClientName) setFetchedClientName(name);
      } catch (err) {
        console.debug(
          "Could not fetch client name from invoices-by-project or invoice detail",
          err?.message || err
        );
      }
    };
    fetchClientName();
    return () => {
      isMounted = false;
    };
  }, [
    selectedInvoice?.project_id,
    selectedInvoice?.projectId,
    project,
    projectData,
    selectedInvoice?.client_name,
    formData.project_id,
    fetchedClientName,
  ]);

  // Fetch budgets for the project to enrich budget/task rows in the table (same flow as InvoiceModal)
  useEffect(() => {
    const pid =
      selectedInvoice?.project_id ||
      selectedInvoice?.projectId ||
      formData.project_id;
    if (!pid) return;
    let isMounted = true;
    const fetchBudgets = async () => {
      try {
        const resp = await Axios.get(`${BUDGET_LIST}${pid}`);
        const data = resp?.data?.data || resp?.data || null;
        if (!isMounted || !data) return;
        setProjectData((prev) => {
          try {
            const next = {
              ...(prev || {}),
              budgets: Array.isArray(data) ? data : [],
            };
            const same = JSON.stringify(prev) === JSON.stringify(next);
            return same ? prev : next;
          } catch (e) {
            return {
              ...(prev || {}),
              budgets: Array.isArray(data) ? data : [],
            };
          }
        });
      } catch (err) {
        console.debug(
          "Could not fetch budgets for project (edit modal)",
          err?.message || err
        );
      }
    };
    fetchBudgets();
    return () => {
      isMounted = false;
    };
  }, [selectedInvoice?.project_id, formData.project_id]);

  // Helpers to format task fields (mirrors InvoiceModal)
  const formatShortDate = (v) => {
    if (!v) return "";
    try {
      const d = new Date(v);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return v || "";
    }
  };
  const formatTaskDate = (t) => {
    const s = t.task_start_date || t.start_date || t.date || t.task_date;
    const e = t.task_end_date || t.end_date;
    if (s && e) return `${formatShortDate(s)} - ${formatShortDate(e)}`;
    return s ? formatShortDate(s) : t.date ? formatShortDate(t.date) : "";
  };
  const formatUser = (t) => {
    if (t && t.assignments && t.assignments[0]) {
      const a = t.assignments[0];
      const full = `${a.first_name || ""} ${a.last_name || ""}`.trim();
      if (full) return full;
    }
    return (
      t.user_fullname ||
      t.user_name ||
      (t.user && t.user.name) ||
      t.assigned_to ||
      "-"
    );
  };
  const formatComment = (t) => t.comment || t.notes || t.description || "";
  const formatWorkedHours = (t) => {
    const hrs =
      t.time_spent ??
      t.worked_hours ??
      t.hours_spent ??
      t.hours ??
      t.total_hours ??
      0;
    const n = typeof hrs === "string" ? parseFloat(hrs) : Number(hrs || 0);
    return `${(isNaN(n) ? 0 : n).toFixed(2)} hr`;
  };
  const formatRate = (t) => {
    const r =
      t.default_billable_rate ?? t.rate ?? t.hourly_rate ?? t.billing_rate;
    if (r === undefined || r === null || r === "") return "-";
    const n = typeof r === "string" ? parseFloat(r) : Number(r || 0);
    if (isNaN(n) || n === 0) return "-";
    return `$${n.toFixed(2)}`;
  };

  const [openBudgets, setOpenBudgets] = useState(() => {
    const initial = {};
    groupedBudgets.forEach((b) => {
      initial[b.bid] = true;
    });
    return initial;
  });

  useEffect(() => {
    const initial = {};
    groupedBudgets.forEach((b) => {
      initial[b.bid] = true;
    });
    setOpenBudgets((prev) => {
      try {
        const same = JSON.stringify(prev) === JSON.stringify(initial);
        return same ? prev : initial;
      } catch (e) {
        return initial;
      }
    });
  }, [groupedBudgets]);

  const toggleBudget = (bid) =>
    setOpenBudgets((p) => ({ ...p, [bid]: !p[bid] }));

  // When the group total is edited, update invoice_items for that budget group.
  const handleGroupTotalChange = (bid, rawValue) => {
    const cleaned = String(rawValue).replace(/[^0-9.\-]/g, "");
    const num = cleaned === "" ? "" : parseFloat(cleaned);
    setFormData((prev) => {
      const items = Array.isArray(prev.invoice_items)
        ? [...prev.invoice_items]
        : [];
      const group = groupedBudgets.find((g) => String(g.bid) === String(bid));
      if (!group) {
        // no group found, append a budget-level item
        return {
          ...prev,
          invoice_items: [
            ...items,
            { budget_id: bid, amount: isNaN(num) ? "" : num },
          ],
        };
      }
      const targetIdxs = group.items
        .map((it) => it._idx)
        .filter((ix) => typeof ix === "number" && ix >= 0 && ix < items.length);
      if (targetIdxs.length === 0) {
        // no existing items, push a new one
        items.push({ budget_id: bid, amount: isNaN(num) ? "" : num });
      } else {
        // set first item to value, others to 0
        for (let i = 0; i < targetIdxs.length; i++) {
          const idx = targetIdxs[i];
          items[idx] = {
            ...items[idx],
            amount: i === 0 ? (isNaN(num) ? "" : num) : 0,
          };
        }
      }
      return { ...prev, invoice_items: items };
    });
    // clear validation for this budget field when user edits
    if (validationFields[`budget_${bid}`]) {
      setValidationFields(prev => { const copy = { ...prev }; delete copy[`budget_${bid}`]; return copy; });
    }
  };

  const subTotal = useMemo(
    () =>
      (formData.invoice_items || [])
        .filter((it) => it && it.amount && !isNaN(parseFloat(it.amount)))
        .reduce((s, it) => s + parseFloat(it.amount), 0),
    [formData.invoice_items]
  );
  const gstAmount = useMemo(
    () => subTotal * (parseFloat(formData.gst) || 0),
    [subTotal, formData.gst]
  );
  const totalAmount = useMemo(
    () => subTotal + gstAmount,
    [subTotal, gstAmount]
  );

  // Determine invoice number to display in header (try multiple common fields)
  const invoiceNo =
    selectedInvoice?.invoice_no ??
    selectedInvoice?.invoiceNo ??
    selectedInvoice?.invoice_number ??
    formData?.invoice_no ??
    selectedInvoice?.reference ??
    selectedInvoice?.id ??
    "";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="new-invoice-dialog"
      contentClassName="new-invoice-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100 d-flex justify-content-between align-items-center">
          <Modal.Title className="mb-0">Edit Invoice - {formData.invoice_title}</Modal.Title>
          {/* <span className="invoice-no">Invoice No: {invoiceNo}</span> */}
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            <strong>Error updating invoice:</strong>
            <br />
            {error}
          </Alert>
        )}

        {!selectedInvoice ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading invoice details...
          </div>
        ) : (
          <Form>
            <div className="invoice-top-grid">
              <div className="grid-left">
                <div className="grid-left-top">
                  <Form.Group className="mb-2">
                    <Form.Label>Invoice To</Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        selectedInvoice?.client_name ||
                        selectedInvoice?.client ||
                        selectedInvoice?.client?.client_name ||
                        project?.client_name ||
                        projectData?.client_name ||
                        fetchedClientName ||
                        ""
                      }
                      readOnly
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Billing Contact <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      name="billing_contact"
                      value={formData.billing_contact}
                      onChange={handleInputChange}
                      isInvalid={!!validationFields.billing_contact}
                      readOnly
                      disabled
                    />
                    <Form.Control.Feedback type="invalid">{validationFields.billing_contact}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <Form.Group className="mb-0">
                  <Form.Label>Invoice Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="invoice_title"
                    value={formData.invoice_title}
                    onChange={handleInputChange}
                    isInvalid={!!validationFields.invoice_title}
                  />
                  <Form.Control.Feedback type="invalid">{validationFields.invoice_title}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    isInvalid={!!validationFields.description}
                  />
                  <Form.Control.Feedback type="invalid">{validationFields.description}</Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="grid-right">
                <div className="top-right-grid">
                  <Form.Group className="mb-2">
                    <Form.Label>Invoice Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="invoice_date"
                      value={formData.invoice_date}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Due Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      isInvalid={!!validationFields.due_date}
                    />
                    <Form.Control.Feedback type="invalid">{validationFields.due_date}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>GST(%)</Form.Label>
                    <Form.Control
                      value={`${(parseFloat(formData.gst) || 0) * 100}`}
                    />
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>Reference</Form.Label>
                    <Form.Control
                      name="reference"
                      value={formData.reference}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* Budgets accordion built from grouped invoice_items */}
            <div className="budget-list-box">
            {groupedBudgets.map((b) => {
              const groupTotal = (b.items || []).reduce(
                (s, it) => s + (parseFloat(it.amount) || 0),
                0
              );
              return (
                <div key={String(b.bid)} className="mb-2 budget-block">
                  <div className="budget-header d-flex align-items-center justify-content-between p-2">
                    <div className="d-flex align-items-center gap-2">
                      {/* <button type="button" className="btn btn-link p-0 budget-toggle" onClick={() => toggleBudget(b.bid)} aria-label="toggle">
                        {openBudgets[b.bid] ? <BsChevronDown size={16} /> : <BsChevronRight size={16} />}
                      </button> */}
                      <span className="text-muted">BD{String(b.bid).padStart(3, "0")} - {b.budget_name}</span>
                    </div>
                    <div style={{ width: 100 }} className="d-flex align-items-center">
                      <div className="d-flex align-items-center w-100">
                        <span className="me-2 text-muted">$</span>
                        <Form.Control
                          className="text-end"
                          type="text"
                          style={{ flex: '1 1 auto' }}
                          isInvalid={!!validationFields[`budget_${b.bid}`]}
                          value={
                            isNaN(Number(groupTotal))
                              ? ""
                              : `${Number(groupTotal).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                          }
                          onChange={(e) =>
                            handleGroupTotalChange(b.bid, e.target.value)
                          }
                          placeholder="0.00"
                        />
                        <Form.Control.Feedback type="invalid">{validationFields[`budget_${b.bid}`]}</Form.Control.Feedback>
                      </div>
                    </div>
                  </div>
                  {false && (
                    <div className="p-2">
                      <Table className="mb-2">
                        <thead>
                          <tr>
                            <th style={{ width: "18%" }}>Date</th>
                            <th style={{ width: "22%" }}>Task</th>
                            <th style={{ width: "18%" }}>User</th>
                            <th>Comment</th>
                            <th style={{ width: "10%" }} className="text-end">
                              Worked Hours
                            </th>
                            <th style={{ width: "10%" }} className="text-end">
                              Rate
                            </th>
                            <th style={{ width: "12%" }} className="text-end">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const projBudgets =
                              projectData?.budgets || project?.budgets || [];
                            const found = projBudgets.find(
                              (pb) => String(pb.bid ?? pb.id) === String(b.bid)
                            );
                            const tasks = Array.isArray(found?.tasks)
                              ? found.tasks
                              : [];
                            const budgetTotal = found
                              ? found.budget ??
                                found.amount ??
                                found.budget_total
                              : undefined;
                            if (tasks.length > 0) {
                              return tasks.map((t, i) => {
                                const amt =
                                  t?.amount ??
                                  t?.cost_spent ??
                                  t?.total_amount ??
                                  budgetTotal ??
                                  groupTotal;
                                const n =
                                  typeof amt === "string"
                                    ? parseFloat(amt)
                                    : Number(amt || 0);
                                return (
                                  <tr key={t.invoice_item_id || t.task_id || i}>
                                    <td>{formatTaskDate(t)}</td>
                                    <td>
                                      {t.task_name ||
                                        t.name ||
                                        t.description ||
                                        "-"}
                                    </td>
                                    <td>{formatUser(t)}</td>
                                    <td>{formatComment(t)}</td>
                                    <td className="text-end">
                                      {formatWorkedHours(t)}
                                    </td>
                                    <td className="text-end">
                                      {formatRate(t)}
                                    </td>
                                    <td className="text-end">
                                      $
                                      {(isNaN(n) ? 0 : n).toLocaleString(
                                        "en-US",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }
                                      )}
                                    </td>
                                  </tr>
                                );
                              });
                            }
                            // Fallback to existing invoice item rows (read-only amounts)
                            return (b.items || []).map((it) => (
                              <tr key={it._idx}>
                                <td>{/* Date not available in raw items */}</td>
                                <td>{it.task_name || "-"}</td>
                                <td>{it.user_fullname || "-"}</td>
                                <td>{it.comment || ""}</td>
                                <td className="text-end">
                                  {Number(it.worked_hours || 0).toFixed(2)} hr
                                </td>
                                <td className="text-end">
                                  {it.rate
                                    ? `$${Number(it.rate).toFixed(2)}`
                                    : "-"}
                                </td>
                                <td className="text-end">
                                  $
                                  {Number(it.amount || 0).toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
            </div>

            <div className="mt-3 d-flex justify-content-end totals-summary">
              <div className="totals-box">
                <div className="d-flex justify-content-between totals-line">
                  <span className="totals-label">Sub Total</span>
                  <span className="totals-value">${subTotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between totals-line">
                  <span className="totals-label">
                    GST {(parseFloat(formData.gst) || 0) * 100}%
                  </span>
                  <span className="totals-value">${gstAmount.toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span>TOTAL (NZD)</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-start gap-2 mt-4">
              <Button
                variant="primary"
                type="button"
                disabled={updateLoading}
                onClick={handleSaveAsDraft}
              >
                {updateLoading ? "Saving..." : "Update Draft"}
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={updateLoading}
                onClick={handleSubmitForApproval}
              >
               Update & Submit to Xero
              </Button>
              <Button variant="secondary" type="button" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditInvoiceModal;
